// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-89 — one protocol slot for THIS patient at enrollment.
//
// Round 2 (owner 2026-10-28): each enrollment keeps the protocol VERSION it was enrolled with; a later change to the
// drug reaches new enrollments only. So the door shows which version this patient is on and, when the drug has
// moved on, offers "Update to the latest version". Staff can also use a different protocol for this patient only.
// Every change needs a reason and is audited; none of them changes the drug.

import { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import AppButton from '../../components/ui/AppButton'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import SearchableSelect from '../../components/common/SearchableSelect'
import type { AdminProtocol } from '../../api/client'
import { PROTOCOL_PIN_NONE, SLOT_CATEGORY, type EnrollmentProtocolSlot, type ProtocolSlot } from '../../api/carerxProtocols'

const LABEL: Record<ProtocolSlot, string> = { premed: 'Premedication', kit: 'Anaphylaxis kit', flush: 'Flushing' }

export type ChoiceKind = 'choice' | 'update_latest'

const chip = { fontSize: 10, fontWeight: 700, borderRadius: 100, px: 1, py: '1px' } as const

export default function PatientProtocolChoice({ slot, presetId, chosenId, saved, savedText, drugText, protocols, onChoose }: {
  slot: ProtocolSlot
  /** The drug's protocol for this slot (null = none assigned). */
  presetId: string | null
  /** This patient's protocol as it stands in the form (null = the drug preset). */
  chosenId: string | null
  /** What the server has for this patient (absent for a new enrollment). */
  saved?: EnrollmentProtocolSlot | null
  /** What THIS patient's saved protocol prints (its pinned version — may be older than the drug's). */
  savedText?: string | null
  /** What the drug's current protocol prints — shown only when it is what applies to this patient
   *  (a new enrollment, or an enrollment not pinned yet); never over a "none" or a pinned version. */
  drugText?: string | null
  /** The pharmacy's current library. */
  protocols: AdminProtocol[]
  onChoose: (id: string | null, reason: string, kind: ChoiceKind) => void
}) {
  const [mode, setMode] = useState<'pick' | 'reset' | 'latest' | null>(null)
  const [picked, setPicked] = useState('')
  const label = LABEL[slot]
  const preset = presetId ? protocols.find(p => p.id === presetId) : undefined
  // Pinned "none": the drug had no protocol for this slot when the patient enrolled (addendum 2026-10-29).
  const isNone = chosenId === PROTOCOL_PIN_NONE
  const options = protocols.filter(p => p.category === SLOT_CATEGORY[slot] && p.active && p.id !== chosenId && (isNone || p.id !== presetId))
  const inLibrary = chosenId ? protocols.find(p => p.id === chosenId) : undefined
  const isSaved = !!saved?.protocol_id && saved.protocol_id === chosenId
  // A different protocol than the drug's = a staff choice; the drug's own protocol (any version) = the enrolled pin.
  const isChoice = chosenId !== null && !isNone && (isSaved ? saved!.chosen : (inLibrary?.code ?? null) !== (preset?.code ?? null))
  const name = isNone ? null : isSaved ? `${saved!.name ?? '—'} · v${saved!.version ?? '?'}` : inLibrary ? `${inLibrary.name} · v${inLibrary.version}` : null
  const behind = isSaved && !isChoice && !saved!.is_latest && !!saved!.latest_id

  // D-CRX-89 (clinical safety, 2026-10-29): the card lists ONLY the lines that apply to this patient — the pinned
  // version's own lines, a pending choice's lines, or (not pinned yet) the drug's. A "none" slot lists no lines.
  const lines = isNone ? null
    : isSaved ? (savedText ?? null)
    : chosenId !== null && inLibrary ? inLibrary.lines.map(l => `\u2022 ${l.item ?? ''}${l.volume_ml ? ` ${l.volume_ml} mL` : ''}${l.sig ? ` — ${l.sig}` : ''}`).join('\n')
    : chosenId === null ? (drugText ?? null)
    : null

  return (
    <Box data-testid={`patient-protocol-${slot}`} sx={{ mt: 1 }}>
      <Box sx={{ fontSize: 12, fontWeight: 700 }}>{label}</Box>
      {isNone && (
        <Box data-testid={`patient-protocol-none-${slot}`}>
          <Box sx={{ fontSize: 12.5, fontWeight: 600 }}>None for this patient</Box>
          {preset && (
            <Box data-testid={`patient-protocol-default-${slot}`} sx={{ fontSize: 11, color: 'var(--color-text-soft)' }}>
              Drug default for new enrollments: {preset.name} v{preset.version} — not applied to this patient
            </Box>
          )}
          <AppButton size="small" variant="outlined" sx={{ mt: 0.5 }} onClick={() => { setPicked(preset?.id ?? ''); setMode('pick') }} disabled={options.length === 0}>
            Add for this patient
          </AppButton>
        </Box>
      )}
      {!isNone && <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        {chosenId !== null && isChoice && (
          <Box component="span" sx={{ ...chip, background: '#fff7e6', color: '#93610f' }}>protocol changed for this patient</Box>
        )}
        {chosenId !== null && !isChoice && (
          <Box component="span" sx={{ ...chip, background: '#eef2fd', color: '#3b5bdb' }}>enrolled version</Box>
        )}
        {name && <Box component="span" sx={{ fontSize: 12, fontWeight: 600 }}>{name}</Box>}
        {chosenId === null && !lines && <Box component="span" sx={{ fontSize: 12, color: 'var(--color-text-soft)' }}>No protocol</Box>}
        {chosenId === null && (
          <AppButton size="small" onClick={() => { setPicked(''); setMode('pick') }} disabled={options.length === 0}>
            Use a different {label.toLowerCase()} protocol for this patient
          </AppButton>
        )}
        {chosenId !== null && (
          <>
            {behind && (
              <AppButton size="small" variant="outlined" onClick={() => setMode('latest')}>
                Update to the latest version (v{saved!.latest_version})
              </AppButton>
            )}
            <AppButton size="small" onClick={() => { setPicked(''); setMode('pick') }} disabled={options.length === 0}>
              {isChoice ? 'Change' : `Use a different ${label.toLowerCase()} protocol`}
            </AppButton>
            {isChoice && presetId && (
              <AppButton size="small" onClick={() => setMode('reset')}>Use the drug's protocol</AppButton>
            )}
          </>
        )}
      </Stack>}
      {lines && (
        <Box data-testid={`patient-protocol-lines-${slot}`} sx={{ fontSize: 11.5, color: 'var(--color-text-soft)', whiteSpace: 'pre-wrap', mt: 0.5 }}>{lines}</Box>
      )}
      {behind && (
        <Box sx={{ fontSize: 11.5, color: '#93610f', mt: 0.5 }}>
          The drug's protocol is now v{saved!.latest_version}. This patient stays on v{saved!.version} until updated.
        </Box>
      )}

      <ConfirmDialog open={mode === 'pick'} title={`${label} protocol for this patient`}
        confirmLabel={isNone ? 'Add for this patient' : 'Use for this patient'} confirmDisabled={!picked}
        requireReason={{ label: isNone ? 'Why add it for this patient?' : 'Why is this patient different?', minLength: 3, placeholder: 'Clinical reason (no patient identifiers needed)' }}
        onCancel={() => setMode(null)}
        onConfirm={({ reason }) => { onChoose(picked, reason.trim(), 'choice'); setMode(null) }}
        body={
          <Stack spacing={1}>
            <Box>Only this patient changes. The drug keeps its protocol.</Box>
            <SearchableSelect ariaLabel={`${label} protocol for this patient`} value={picked} onChange={setPicked}
              placeholder="Pick a protocol…"
              options={options.map(p => ({ value: p.id, label: `${p.name} · v${p.version}` }))} />
          </Stack>
        } />
      <ConfirmDialog open={mode === 'reset'} title={`Use the drug's ${label.toLowerCase()} protocol again?`}
        confirmLabel="Use the drug's protocol"
        requireReason={{ label: 'Reason', minLength: 3 }}
        onCancel={() => setMode(null)}
        onConfirm={({ reason }) => { onChoose(presetId, reason.trim(), 'choice'); setMode(null) }} />
      <ConfirmDialog open={mode === 'latest'} title={`Update this patient to ${saved?.name ?? 'the protocol'} v${saved?.latest_version ?? ''}?`}
        confirmLabel="Update to the latest version"
        requireReason={{ label: 'Reason', minLength: 3, placeholder: 'Why this patient moves to the new version' }}
        onCancel={() => setMode(null)}
        onConfirm={({ reason }) => { if (saved?.latest_id) onChoose(saved.latest_id, reason.trim(), 'update_latest'); setMode(null) }}
        body={<Box>Only this patient moves. Plans of Treatment already started keep the version they were made with.</Box>} />
    </Box>
  )
}
